import {observer} from "mobx-react";
import {FormSelect} from "@vidazoo/ui-kit";
import theme from "../theme.module.scss";
import * as React from "react";
import {IChangeLogFilter, IChangeLogUserValue} from "../../../common/interfaces/IChangeLogFilter";
import {FilterOperators} from '../../../../../common/enums/FilterOperators';
import useChangeLogsModel from '../../../common/hooks/useChangeLogsModel';
import {AsyncPaginate} from "react-select-async-paginate";
import {useInjex} from "@injex/react-plugin";
import {UsersApi} from "../../../../../common/api/UsersApi.mdl";
import {IAuthManagerUser} from "../../../../../common/interfaces/IAuthManagerUser";

export interface IUsersFilterProps extends React.InputHTMLAttributes<HTMLInputElement> {
    filter: IChangeLogFilter;
}

type UserOption = {value: string; label: string; legacyId?: string};

const selectStyles = {
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
    control: (base, state) => ({
        ...base,
        minHeight: "3.2rem",
        fontSize: "var(--select-font-size)",
        color: "var(--select-color)",
        fontWeight: "var(--select-font-weight)",
        fontFamily: "var(--select-font-family)",
        padding: "0 0 0 var(--select-padding)",
        borderRadius: "var(--select-border-radius)",
        border: "var(--select-border)",
        position: "relative",
        cursor: "pointer",
        backgroundColor: "var(--select-background-color)",
        transition: "border-color 200ms",
        boxShadow: "none",
        borderColor: state.isFocused
            ? "var(--select-border-focus, #2684ff)"
            : undefined,
        "&:hover": {
            borderColor: "var(--select-border-hover, #999)"
        }
    }),
    menu: (base) => ({
        ...base,
        borderRadius: "var(--select-border-radius)",
        backgroundColor: "var(--select-background-color)"
    }),
    option: (base, state) => ({
        ...base,
        fontSize: "var(--select-font-size)",
        fontFamily: "var(--select-font-family)",
        fontWeight: "var(--select-font-weight)",
        color: state.isSelected
            ? "var(--select-option-selected-color)"
            : "var(--select-color)",
        backgroundColor: state.isSelected
            ? "var(--select-option-selected-bg)"
            : state.isFocused
                ? "var(--select-option-hover-bg)"
                : "transparent",
        cursor: "pointer"
    })
};

function toUserOption(user: IAuthManagerUser): UserOption {
    const value = user.user_id;
    const name = [user.given_name, user.family_name].filter(Boolean).join(" ");
    const label = user.email && name
        ? `${user.email} (${name})`
        : (user.email || value);
    const legacyId = user.app_metadata
        && user.app_metadata.apps
        && user.app_metadata.apps.supply
        && user.app_metadata.apps.supply.legacyId;

    return {value, label, legacyId};
}

function toSelectedOptions(values: IChangeLogUserValue[]): UserOption[] {
    return (values || []).map((user) => ({
        value: user.user_id || (user as unknown as string),
        label: user.user_id || (user as unknown as string),
        legacyId: user.legacyId
    }));
}

function UsersFilter({filter}: IUsersFilterProps): React.JSX.Element {
    const model = useChangeLogsModel();
    const [inject] = useInjex();
    const usersApi = inject<UsersApi>("usersApi");
    const [selectedUsers, setSelectedUsers] = React.useState<UserOption[]>(() => toSelectedOptions(filter.value));

    const onChangeFilterParam = (e) => {
        const {name, value} = e.target;
        model.setFilterParam(filter, name, value);
    };

    const loadOptions = async (search: string, loadedOptions: UserOption[], additional: {page: number}) => {
        const page = additional && typeof additional.page === "number" ? additional.page : 0;

        if (search && search.length <= 2) {
            return {
                options: [],
                hasMore: false,
                additional: {page}
            };
        }

        const {data} = await usersApi.getUsers({page, q: search});
        const users = data || [];

        return {
            options: users.map(toUserOption),
            hasMore: users.length > 0,
            additional: {page: page + 1}
        };
    };

    const onChangeUsers = (options: UserOption[]) => {
        const next = options || [];
        setSelectedUsers(next);
        model.setFilterParam(filter, "value", next.map((option) => ({
            user_id: option.value,
            legacyId: option.legacyId
        })));
    };

    return (
        <>
            <FormSelect
                className={theme.select}
                items={[FilterOperators.isAnyOf, FilterOperators.isNotAnyOf]}
                maxItems={5}
                itemValueKey="value"
                itemLabelKey="label"
                value={filter.operator || FilterOperators.isAnyOf}
                name="operator"
                required
                hideValidationMessage
                hideWhiteSpacesMessage={true}
                onChange={onChangeFilterParam}
                searchable
            />
            <div className={theme.usersSelect}>
                <AsyncPaginate
                    isMulti
                    isClearable
                    debounceTimeout={300}
                    value={selectedUsers}
                    onChange={onChangeUsers}
                    loadOptions={loadOptions}
                    additional={{page: 0}}
                    defaultOptions
                    filterOption={null}
                    styles={selectStyles}
                    placeholder="Search users"
                    closeMenuOnSelect={false}
                    menuPortalTarget={document.body}
                    noOptionsMessage={({inputValue}) =>
                        inputValue.length > 0 && inputValue.length <= 2
                            ? "Type at least 3 characters to search"
                            : "No users found"
                    }
                />
            </div>
        </>
    );
}

export default observer(UsersFilter);
